import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";

import type { HealthCounts, ServerHealthFilter, ServerTone } from "../serverHealth.js";
import { toneColor } from "./serverPills.js";

/* ---------- Empty state (no servers monitored at all) -------------- */

export function EmptyState({
  canManage,
  onAdd,
}: {
  canManage: boolean;
  onAdd?: (() => void) | undefined;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        borderRadius: 1,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",
        px: 3,
        py: 6,
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
        No servers being monitored yet.
      </Typography>
      {canManage && onAdd ? (
        <Button size="small" variant="contained" disableElevation sx={{ mt: 1.5 }} onClick={onAdd}>
          Add the first one
        </Button>
      ) : (
        <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
          Ask an admin with{" "}
          <Box component="code" sx={{ fontFamily: "ui-monospace, monospace" }}>
            server:manage
          </Box>{" "}
          permission to add one.
        </Typography>
      )}
    </Box>
  );
}

/* ---------- Health filter ------------------------------------------ */

/** All / Healthy / Broken pill row. Counts come from the full payload. */
export function ServerFilterBar({
  filter,
  counts,
  onChange,
}: {
  filter: ServerHealthFilter;
  counts: HealthCounts;
  onChange: (next: ServerHealthFilter) => void;
}): JSX.Element {
  const options: Array<[ServerHealthFilter, string]> = [
    ["all", `All (${counts.all})`],
    ["healthy", `Healthy (${counts.healthy})`],
    ["broken", `Broken (${counts.broken})`],
  ];
  return (
    <Box
      role="group"
      aria-label="Filter servers by health"
      data-testid="server-filter"
      sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
    >
      {options.map(([value, label]) => (
        <FilterPill
          key={value}
          tone={value}
          active={filter === value}
          onClick={() => onChange(value)}
          data-testid={`server-filter-${value}`}
        >
          {label}
        </FilterPill>
      ))}
    </Box>
  );
}

function FilterPill({
  active,
  tone,
  onClick,
  children,
  "data-testid": testId,
}: {
  active: boolean;
  tone: ServerHealthFilter;
  onClick: () => void;
  children: ReactNode;
  "data-testid"?: string;
}): JSX.Element {
  const dotTone: ServerTone | null =
    tone === "healthy" ? "good" : tone === "broken" ? "bad" : null;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      {...(testId ? { "data-testid": testId } : {})}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 999,
        px: 1.25,
        py: 0.5,
        fontSize: 11,
        fontWeight: 500,
        cursor: "pointer",
        transition: "background-color 150ms, color 150ms",
        ...(active
          ? { border: 0, bgcolor: "primary.main", color: "primary.contrastText" }
          : {
              border: 1,
              borderColor: "divider",
              bgcolor: "transparent",
              color: "text.secondary",
              "&:hover": { bgcolor: "surfaceSoft" },
            }),
      }}
    >
      {dotTone && (
        <Box
          aria-hidden
          sx={{
            height: 6,
            width: 6,
            borderRadius: "50%",
            bgcolor: active
              ? (t) => alpha(t.palette.primary.contrastText, 0.9)
              : (t) => toneColor(t, dotTone),
          }}
        />
      )}
      {children}
    </Box>
  );
}

/** Shown when the active filter matches none of the monitored servers. */
export function EmptyFilterState({
  filter,
}: {
  filter: ServerHealthFilter;
}): JSX.Element {
  const label = filter === "healthy" ? "fully healthy" : "broken";
  return (
    <Box
      data-testid="server-filter-empty"
      sx={{
        borderRadius: 1,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "background.paper",
        px: 3,
        py: 5,
        textAlign: "center",
        fontSize: 14,
        color: "text.secondary",
      }}
    >
      No {label} servers right now.
    </Box>
  );
}
